<template>
  <div
    ref="header"
    class="fixed top-0 z-50 bg-[#F2EFED] transition-transform duration-300 w-full safe-area"
    :style="{ transform: `translateY(${headerTransform}px)` }"
  >
    <div
      class="flex items-center justify-between px-[20px] pt-[16px] pb-[8px]"
      :class="{ '!pt-[0px]': isIos() }"
    >
      <!-- :class="{ '!pt-[60px]': isIos() }"> -->
      <slot name="left"> </slot>
      <slot name="center">
        <h1 class="text-lg font-semibold">{{ title }}</h1>
      </slot>
      <slot name="right"></slot>
    </div>
    <slot name="bottom"></slot>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { isIosNativeOnly } from '@/utils/platform-check';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  peekHeight: {
    type: Number,
    default: 10,
  },
  headerHeight: {
    type: Number,
    default: 48,
  },
  enablePeek: {
    type: Boolean,
    default: true,
  },
  scrollElementId: {
    type: String,
  },
  modelValue: {
    type: Boolean,
  },
  preventBounceReveal: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);
const header = ref(null);
const headerTransform = ref(0);
let lastScrollTop = 0;
let hasUserScrolled = false;

const isIos = () => {
  return isIosNativeOnly();
};

const getSafeAreaInsets = () => {
  const el = header.value;
  if (!el) return { top: 0, bottom: 0, left: 0, right: 0 };
  const styles = getComputedStyle(el);
  return {
    top: parseInt(styles.paddingTop) || 0,
    bottom: parseInt(styles.paddingBottom) || 0,
    left: parseInt(styles.paddingLeft) || 0,
    right: parseInt(styles.paddingRight) || 0,
  };
};

const hideHeader = heightOverride => {
  const headerHeight = heightOverride || header.value?.offsetHeight || props.headerHeight;
  if (!headerHeight) return;
  const insets = getSafeAreaInsets();
  const iosInset = isIos() && props.peekHeight > 10 ? insets.top : 0;
  const peekValue = Math.max(props.peekHeight, 0);
  const peekOffset = Math.min(peekValue + iosInset, headerHeight);
  headerTransform.value = -(headerHeight - peekOffset);
  emit('update:modelValue', true);
  hasUserScrolled = true;
};

const showHeader = () => {
  headerTransform.value = 0;
  emit('update:modelValue', false);
};

const onScroll = async event => {
  if (!props.enablePeek) return;

  const scrollElement = await document.getElementById(props.scrollElementId)?.getScrollElement();

  const currentScroll = scrollElement ? scrollElement.scrollTop : window.scrollY;
  const headerHeight = header.value?.offsetHeight || props.headerHeight;

  if (currentScroll > 0) {
    hasUserScrolled = true;
  }

  if (
    props.preventBounceReveal &&
    isIos() &&
    hasUserScrolled &&
    currentScroll <= 0 &&
    headerTransform.value < 0
  ) {
    hideHeader(headerHeight);
    lastScrollTop = currentScroll;
    return;
  }

  // Scrolling down
  if (currentScroll < 0 || lastScrollTop < 0) {
    if (props.preventBounceReveal && isIos() && headerTransform.value < 0) {
      hideHeader(headerHeight);
    }
    lastScrollTop = currentScroll;
    return;
  }
  if (currentScroll > lastScrollTop) {
    hideHeader(headerHeight);
  }
  // Scrolling up
  else {
    if (props.preventBounceReveal && isIos() && currentScroll <= 0 && headerTransform.value < 0) {
      hideHeader(headerHeight);
    } else {
      showHeader();
    }
  }

  lastScrollTop = currentScroll;
};

// Add debouncing to prevent excessive calculations
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

const debouncedScroll = debounce(onScroll, 10);

watch(
  () => props.enablePeek,
  newVal => {
    if (!newVal) {
      showHeader();
      hasUserScrolled = false;
    }
  },
);

onMounted(() => {
  document
    .getElementById(props.scrollElementId)
    ?.getScrollElement()
    .then(scrollElement => {
      !scrollElement
        ? window.addEventListener('scroll', debouncedScroll, { passive: true })
        : scrollElement.addEventListener('scroll', debouncedScroll, { passive: true });
    });
});

onUnmounted(() => {
  document
    .getElementById(props.scrollElementId)
    ?.getScrollElement()
    .then(scrollElement => {
      !scrollElement
        ? window.removeEventListener('scroll', debouncedScroll)
        : scrollElement.removeEventListener('scroll', debouncedScroll);
    });
});
</script>

<style scoped>
/* Safe Area View styling for iOS */
.safe-area {
  padding-top: env(safe-area-inset-top);
}
</style>
